/**
 * The web app: sign in, upload screenshots, edit a set, export a ZIP.
 *
 * Presentation only. Every operation here is the one that was already verified — the same renderer,
 * the same export-in-the-tab, the same validation. The redesign changed the world it is shown in
 * (DESIGN.md) and the shape of the workbench, not what the product does.
 *
 * The layout has three columns because the work has three kinds of object: **panels** (positions in
 * a set), **layers** (what is drawn on this panel), and the **canvas** (the size being produced).
 * Panels and layers are separate lists on purpose; stacking them into one column was the previous
 * version's central confusion.
 */
import { createBrowserEnvironment } from '@keymaker/renderer/browser';
import {
  ELEMENTS,
  elementById,
  elementsByCategory,
  prepare3dDevices,
  renderScene,
  substituteTokens,
  type ElementDefinition,
} from '@keymaker/renderer';
import type { RenderImage } from '@keymaker/renderer';
import type { Background, DeviceFrame, OutputFamily, SceneDoc } from '@keymaker/schema';
import { DEFAULT_FAMILIES, Layer, bindPanel, familyByKey, layerPatch, templateIdForPanel, tokenValues } from '@keymaker/schema';
import { resolveForFamily } from '@keymaker/templates';
import {
  api,
  frameImages,
  frames,
  renderSet,
  zipSet,
  type PanelSummary,
  type ProjectBundle,
  type ProjectSummary,
  type TemplateSummary,
} from './client';
import { Editor } from './editor';

// ------------------------------------------------------------------------ utilities

type Child = Node | string | null | undefined | false;

function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Record<string, unknown> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  for (const [key, value] of Object.entries(props)) {
    if (key === 'dataset') Object.assign(element.dataset, value as Record<string, string>);
    else if (key.startsWith('on') && typeof value === 'function') {
      element.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (key === 'class') element.className = String(value);
    else if (value !== null && value !== undefined && value !== false) {
      element.setAttribute(key, String(value));
    }
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    element.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }
  return element;
}

/**
 * Icons are authored SVG at one stroke weight, not unicode glyphs.
 *
 * The previous version used `●` and `○` for visibility, which renders at whatever size and weight
 * the font decides and belongs to no system.
 */
const ICONS: Record<string, string> = {
  grip: '<circle cx="3" cy="3" r="1"/><circle cx="9" cy="3" r="1"/><circle cx="3" cy="8" r="1"/><circle cx="9" cy="8" r="1"/><circle cx="3" cy="13" r="1"/><circle cx="9" cy="13" r="1"/>',
  eye: '<path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5Z"/><circle cx="8" cy="8" r="2"/>',
  eyeOff: '<path d="M6.3 3.3A6.8 6.8 0 0 1 8 3c4.5 0 7 5 7 5a12 12 0 0 1-2.2 2.8M4 4.6A12 12 0 0 0 1 8s2.5 5 7 5a6.6 6.6 0 0 0 2.4-.4"/><path d="M2 2l12 12"/>',
  up: '<path d="M4 10l4-4 4 4"/>',
  down: '<path d="M4 6l4 4 4-4"/>',
  download: '<path d="M8 2v8m0 0 3-3m-3 3-3-3"/><path d="M2.5 12.5h11"/>',
  upload: '<path d="M8 12V4m0 0 3 3m-3-3-3 3"/><path d="M2.5 13.5h11"/>',
  trash: '<path d="M3 5h10M6 5V3h4v2M4.5 5l.6 9h5.8l.6-9"/>',
  copy: '<rect x="5.5" y="5.5" width="8" height="8" rx="1.5"/><path d="M10.5 5.5V4a1.5 1.5 0 0 0-1.5-1.5H4A1.5 1.5 0 0 0 2.5 4v5A1.5 1.5 0 0 0 4 10.5h1.5"/>',
  plus: '<path d="M8 3.5v9M3.5 8h9"/>',
  swap: '<path d="M2.5 5.5h9l-2.5-2.5M13.5 10.5h-9l2.5 2.5"/>',
};

function icon(name: keyof typeof ICONS | string, size = 16): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.5');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.innerHTML = ICONS[name] ?? '';
  return svg;
}

/** A filename reduced to a screen identifier, so the manifest records something meaningful. */
export function screenNameFrom(filename: string): string {
  const base = filename.replace(/\.[a-z0-9]+$/i, '');
  const cleaned = base
    .replace(/^[0-9]+[-_ ]*/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return cleaned.length > 0 ? cleaned : 'screen';
}

function download(filename: string, bytes: Uint8Array): void {
  const blob = new Blob([bytes as unknown as BlobPart], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const anchor = h('a', { href: url, download: filename });
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

let toastTimer: number | undefined;
function toast(message: string, bad = false): void {
  document.querySelector('.toast')?.remove();
  window.clearTimeout(toastTimer);
  const node = h('div', { class: `toast${bad ? ' bad' : ''}`, role: 'status' }, message);
  document.body.append(node);
  toastTimer = window.setTimeout(() => node.remove(), bad ? 7000 : 3400);
}

// ---------------------------------------------------------------------------- state

interface State {
  user: { email: string } | null;
  templates: TemplateSummary[];
  projects: ProjectSummary[];
  bundle: ProjectBundle | null;
  family: string;
  /**
   * Which screen the app is on.
   *
   * There were two: a single-panel editor and a board. The editor is gone — every panel is editable on
   * the board, so a second screen that edits one of them was a mode switch with nothing to switch for —
   * and in its place is the set view, where the upload happens and every template is previewed.
   */
  screen: 'projects' | 'gallery' | 'board';
  panelPosition: number;
  frames: DeviceFrame[];
  assets: Map<string, RenderImage>;
  busy: string | null;
  /**
   * What happened to each file in the last upload.
   *
   * An upload that fails silently is indistinguishable from one that worked, and the failures are all
   * file-specific: a HEIC photo, a file over the limit, a set that is already full. The screen lists
   * them, so nothing about an upload is invisible.
   */
  uploads: UploadReport[];
}

interface UploadReport {
  name: string;
  bytes: number;
  status: 'added' | 'rejected';
  reason?: string;
}

const state: State = {
  user: null,
  templates: [],
  projects: [],
  bundle: null,
  family: 'iphone',
  screen: 'gallery',
  panelPosition: 1,
  frames: [],
  assets: new Map(),
  busy: null,
  uploads: [],
};

const root = document.getElementById('root')!;
/** The screen currently built, so a redraw can repaint its panels without rebuilding it. */
let activeScreen: HTMLElement | null = null;

/** Recent render timings, in milliseconds. A ring of numbers, because it costs nothing. */
const renderTimings: { at: number; body: number; composite: number }[] = [];

/**
 * The editors of the board, one per panel, keyed by position.
 *
 * Every panel being editable at once means every panel needs its own editor: its own canvas, its own
 * hit-testing and its own selection. Rebuilt whenever the board is, and cleared when the single-panel
 * view takes over so a stale card cannot answer for a panel that is no longer on screen.
 */
const boardEditors = new Map<number, Editor>();

/**
 * One environment for the whole tab.
 *
 * `render3d` builds a WebGL renderer and an environment map on first use; a fresh environment per
 * render would rebuild both, so the shared one is what makes dragging a rotation slider viable.
 */
let sharedEnv: ReturnType<typeof createBrowserEnvironment> | null = null;
function editorEnv(): ReturnType<typeof createBrowserEnvironment> {
  return (sharedEnv ??= createBrowserEnvironment());
}

/**
 * The editor the inspector, the layer list and the layer controls are driving: the selected panel's.
 *
 * Everything downstream asks this instead of reaching for a module-level variable, which is what lets
 * an edit land on the panel the user actually clicked.
 */
function currentEditor(): Editor | null {
  return boardEditors.get(state.panelPosition) ?? null;
}

const family = (): OutputFamily => familyByKey(DEFAULT_FAMILIES, state.family) ?? DEFAULT_FAMILIES[0]!;

// ----------------------------------------------------------------------- auth screen

function authScreen(): HTMLElement {
  const email = h('input', { type: 'email', name: 'email', placeholder: 'you@example.com', autocomplete: 'email' });
  const password = h('input', {
    type: 'password',
    name: 'password',
    placeholder: 'at least 10 characters',
    autocomplete: 'current-password',
  });
  const problem = h('p', { class: 'note warn', role: 'alert', hidden: 'hidden' });

  const submit = async (mode: 'login' | 'signup') => {
    problem.hidden = true;
    try {
      await (mode === 'login' ? api.login(email.value, password.value) : api.signup(email.value, password.value));
      await boot();
    } catch (error) {
      problem.textContent = (error as Error).message;
      problem.hidden = false;
    }
  };

  return h(
    'div',
    { class: 'centered' },
    h('h1', {}, 'App Screenshots'),
    h('p', { class: 'lede' }, 'Turn the screenshots you already have into App Store sets.'),
    h(
      'form',
      {
        class: 'panel-form',
        onsubmit: (event: Event) => {
          event.preventDefault();
          void submit('login');
        },
      },
      h('div', { class: 'field' }, h('span', { class: 'name' }, 'Email'), email),
      h('div', { class: 'field' }, h('span', { class: 'name' }, 'Password'), password),
      problem,
      h(
        'div',
        { class: 'button-row' },
        h('button', { class: 'primary', type: 'submit', 'data-action': 'login' }, 'Sign in'),
        h('button', { type: 'button', 'data-action': 'signup', onclick: () => void submit('signup') }, 'Create account'),
      ),
    ),
  );
}

// ------------------------------------------------------------------- projects screen

function projectsScreen(): HTMLElement {
  const name = h('input', { type: 'text', placeholder: 'Pitch Fox', 'data-field': 'project-name' });
  const template = h(
    'select',
    { 'data-field': 'template' },
    ...state.templates.map((entry) => h('option', { value: entry.id }, entry.name)),
  );
  const familyInputs = DEFAULT_FAMILIES.map((entry) =>
    h(
      'label',
      { class: 'row', style: 'grid-template-columns:18px 1fr; cursor:pointer' },
      h('input', {
        type: 'checkbox',
        value: entry.key,
        checked: entry.key === 'iphone' ? 'checked' : undefined,
        'data-family': entry.key,
      }),
      h('span', {}, `${entry.label} · ${entry.canvas.width}×${entry.canvas.height}`),
    ),
  );

  const create = async () => {
    const chosen = familyInputs
      .map((node) => node.querySelector('input')!)
      .filter((input) => input.checked)
      .map((input) => input.value);
    if (chosen.length === 0) return toast('Choose at least one device size.', true);
    try {
      const created = await api.createProject(name.value || 'Untitled', template.value, chosen);
      await openProject(created.id);
    } catch (error) {
      toast((error as Error).message, true);
    }
  };

  return h(
    'div',
    { class: 'catalogue' },
    h('header', {}, h('h1', {}, 'Projects'), h('span', { class: 'measure' }, `${state.projects.length} total`)),
    state.projects.length === 0
      ? h('p', { class: 'note info' }, 'No projects yet. Create one below.')
      : h(
          'div',
          { class: 'projects', 'data-role': 'projects' },
          ...state.projects.map((project) =>
            h(
              'button',
              {
                class: 'project-row',
                'data-project': project.id,
                onclick: () => void openProject(project.id),
              },
              h('span', { class: 'name' }, project.name),
              h('span', { class: 'facts' }, `${project.asset_count} uploads · ${project.panel_count} panels`),
              h('span', { class: 'facts' }, project.families.map((key) => familyByKey(DEFAULT_FAMILIES, key)?.label ?? key).join(' · ')),
            ),
          ),
        ),
    h(
      'section',
      { style: 'margin-top:36px;max-width:420px' },
      h('h2', { style: 'font-size:15px;margin-bottom:14px' }, 'New project'),
      h('div', { class: 'field' }, h('span', { class: 'name' }, 'App name'), name),
      h('div', { class: 'field' }, h('span', { class: 'name' }, 'Template'), template),
      h('div', { class: 'field' }, h('span', { class: 'name' }, 'Device sizes'), ...familyInputs),
      h('button', { class: 'primary', 'data-action': 'create-project', onclick: () => void create() }, 'Create project'),
    ),
  );
}

// ------------------------------------------------------------------- sidebar: panels

let draggingPosition: number | null = null;

/**
 * Panels, draggable to reorder.
 *
 * Order is the whole story of a store listing — the first two panels decide whether anyone sees the
 * third — so it is a direct manipulation rather than something set through a form. Positions are
 * renumbered on drop and saved, because the manifest's order has to match what the user sees.
 */
function panelsGroup(bundle: ProjectBundle): HTMLElement {
  const rows = [...bundle.panels].sort((a, b) => a.position - b.position);

  const clearMarks = (): void => {
    document
      .querySelectorAll('.row.drop-before, .row.drop-after')
      .forEach((node) => node.classList.remove('drop-before', 'drop-after'));
  };
  /** Which side of the row the pointer is on, measured on the row's own box. */
  const landsAfter = (row: HTMLElement, clientY: number): boolean => {
    const rect = row.getBoundingClientRect();
    return clientY - rect.top > rect.height / 2;
  };

  const items = rows.map((panel, index) => {
    const row = h(
      'div',
      {
        class: 'row',
        'data-panel': String(panel.position),
        'aria-selected': panel.position === state.panelPosition ? 'true' : 'false',
        role: 'button',
        tabindex: '0',
        onkeydown: (event: KeyboardEvent) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          selectPanel(panel.position);
        },
        onclick: () => selectPanel(panel.position),
        ondragover: (event: DragEvent) => {
          if (draggingPosition === null || draggingPosition === panel.position) return;
          event.preventDefault();
          const after = landsAfter(row, event.clientY);
          row.classList.toggle('drop-before', !after);
          row.classList.toggle('drop-after', after);
        },
        ondragleave: () => row.classList.remove('drop-before', 'drop-after'),
        ondrop: (event: DragEvent) => {
          event.preventDefault();
          clearMarks();
          if (draggingPosition === null || draggingPosition === panel.position) return;
          void movePanel(draggingPosition, index, landsAfter(row, event.clientY));
        },
      },
      /**
       * Only the handle is draggable, not the row.
       *
       * With `draggable` on the row, Chromium treats a plain press as the start of a native drag and
       * never delivers the click — so "click to select" and "drag to reorder" silently cancelled each
       * other out for the user, not just for the test. Splitting them also gives the drag its own
       * affordance, which is the pattern every list that does both uses.
       */
      h(
        'span',
        {
          class: 'grip',
          draggable: 'true',
          title: 'Drag to reorder',
          'aria-hidden': 'true',
          ondragstart: (event: DragEvent) => {
            draggingPosition = panel.position;
            event.dataTransfer?.setData('text/plain', String(panel.position));
            row.classList.add('dragging');
          },
          ondragend: () => {
            row.classList.remove('dragging');
            clearMarks();
          },
        },
        icon('grip', 14),
      ),
      h('span', { class: 'name' }, panel.screen),
      // The position is what the row is for, so it stays until the pointer is on the row — then it
      // gets out of the way of the two things you can do to the panel.
      h('span', { class: 'ordinal' }, String(panel.position)),
      h(
        'span',
        { class: 'actions' },
        h(
          'button',
          {
            class: 'icon-button',
            title: 'Duplicate this panel',
            'data-action': 'duplicate-panel',
            onclick: (event: Event) => {
              event.stopPropagation();
              void duplicatePanel(panel.position);
            },
          },
          icon('copy', 14),
        ),
        h(
          'button',
          {
            class: 'icon-button',
            title: 'Delete this panel',
            'data-action': 'delete-panel',
            onclick: (event: Event) => {
              event.stopPropagation();
              void deletePanel(panel.position);
            },
          },
          icon('trash', 14),
        ),
      ),
    );
    return row;
  });

  return h(
    'div',
    { class: 'group' },
    h('span', { class: 'label' }, `Panels \u00b7 ${rows.length}`),
    rows.length === 0
      ? h('p', { class: 'status' }, 'Nothing yet. Upload screenshots below.')
      : h('div', { class: 'stack', 'data-role': 'panels' }, ...items),
    h(
      'label',
      { class: 'button-row', style: 'margin-top:9px' },
      h('input', {
        type: 'file',
        accept: 'image/png,image/jpeg',
        multiple: 'multiple',
        'data-action': 'upload',
        hidden: 'hidden',
        onchange: (event: Event) => void uploadSet((event.target as HTMLInputElement).files),
      }),
      h('button', {
        type: 'button',
        style: 'flex:1',
        'data-action': 'pick-files',
        onclick: (event: Event) =>
          ((event.currentTarget as HTMLElement).parentElement?.querySelector('input') as HTMLInputElement)?.click(),
      }, icon('upload'), ' Add screenshots'),
    ),
    h(
      'button',
      {
        type: 'button',
        style: 'width:100%;margin-top:6px',
        'data-action': 'add-panel',
        title: 'A designed slide with no screenshot',
        onclick: () => void addPanel(),
      },
      icon('plus', 14),
      ' Add empty panel',
    ),
  );
}

/** Switching panels re-binds the capture; the render pass does that, so this only records which one. */
function selectPanel(position: number): void {
  state.panelPosition = position;
  renderScreen();
}

async function movePanel(from: number, targetIndex: number, after: boolean): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  const rows = [...bundle.panels].sort((a, b) => a.position - b.position);
  const moving = rows.find((panel) => panel.position === from);
  if (!moving) return;
  const fromIndex = rows.findIndex((panel) => panel.position === from);
  if (fromIndex < 0 || targetIndex < 0) return;

  const rest = rows.filter((_, index) => index !== fromIndex);
  /**
   * The insert point is measured in the list **without** the moving panel.
   *
   * `targetIndex` counts rows in the full list, and taking the panel out shifts every index after it
   * down by one. Without that correction a panel dropped below a row landed one place too far — drag
   * the first panel onto the second and it went to the end, which is a set in the wrong order that
   * looks like it worked.
   */
  let insertAt = after ? targetIndex + 1 : targetIndex;
  if (fromIndex < insertAt) insertAt -= 1;
  rest.splice(insertAt, 0, moving);

  // Renumber from the new order: positions are 1..n with no gaps, and the manifest is built from
  // them, so a display-only reorder would ship the wrong sequence.
  // Every field travels with the panel: a reorder changes the order, not anyone's edits.
  const panels = rest.map((panel, index) => ({ ...wirePanel(panel), position: index + 1 }));

  try {
    await api.setPanels(bundle.id, panels);
    await openProject(bundle.id);
    toast('Order saved.');
  } catch (error) {
    toast((error as Error).message, true);
  }
}

/**
 * The set-level background.
 *
 * It lives beside the panels rather than inside one, because it is the thing they all follow — and
 * each of them can stop following it from the inspector.
 */
function projectGroup(bundle: ProjectBundle): HTMLElement {
  return h(
    'div',
    { class: 'group' },
    h('span', { class: 'label' }, 'Background'),
    h(
      'div',
      { class: 'field' },
      h('span', { class: 'name' }, 'Every panel follows this'),
      backgroundEditor({
        kind: backgroundKindOf(bundle.background),
        value: bundle.background ?? DEFAULT_BACKGROUND,
        inheritLabel: "Each template's own",
        apply: (next) => void saveProjectBackground(next),
      }),
    ),
    h(
      'p',
      { class: 'status' },
      'A panel can keep a background of its own — set it from the inspector once that panel is selected.',
    ),
  );
}

function layersGroup(scene: SceneDoc): HTMLElement {
  const selected = currentEditor()?.selection ?? null;
  return h(
    'div',
    { class: 'group' },
    h('span', { class: 'label' }, `Layers · ${scene.layers.length}`),
    h(
      'div',
      { class: 'stack', 'data-role': 'layers' },
      ...[...scene.layers].reverse().map((layer) =>
        h(
          'div',
          {
            class: 'row',
            'data-layer': layer.id,
            'aria-selected': layer.id === selected ? 'true' : 'false',
            role: 'button',
            tabindex: '0',
            // Selecting is not a rebuild: the editor reports the change, and the board repaints its
            // lists from the callback.
            onclick: () => currentEditor()?.select(layer.id),
          },
          h('button', {
            class: 'icon-button',
            style: 'width:18px;height:18px',
            'data-action': 'toggle-visible',
            'aria-pressed': layer.visible ? 'true' : 'false',
            title: layer.visible ? 'Hide layer' : 'Show layer',
            onclick: (event: Event) => {
              event.stopPropagation();
              currentEditor()?.toggleVisible(layer.id);
              repaintWorkbench();
            },
          }, icon(layer.visible ? 'eye' : 'eyeOff', 14)),
          h('span', { class: 'name' }, humanise(layer.name ?? layer.id)),
          // The type is only worth a column when it is not already the name; "Device  device" is the
          // kind of duplication that makes a careful list look careless.
          h('span', { class: 'meta' }, layer.type === (layer.name ?? layer.id) ? '' : layer.type),
          h(
            'span',
            { class: 'actions' },
            h(
              'button',
              {
                class: 'icon-button',
                title: 'Remove this layer from this panel',
                'data-action': 'remove-layer',
                onclick: (event: Event) => {
                  event.stopPropagation();
                  void removeLayer(state.panelPosition, layer.id);
                },
              },
              icon('trash', 14),
            ),
          ),
        ),
      ),
    ),
    selected
      ? h(
          'div',
          { class: 'button-row', style: 'margin-top:9px' },
          h('button', {
            'data-action': 'raise',
            title: 'Bring forward',
            onclick: () => {
              currentEditor()?.reorder(selected, 1);
              repaintWorkbench();
            },
          }, icon('up', 14), ' Forward'),
          h('button', {
            'data-action': 'lower',
            title: 'Send backward',
            onclick: () => {
              currentEditor()?.reorder(selected, -1);
              repaintWorkbench();
            },
          }, icon('down', 14), ' Back'),
        )
      : null,
  );
}

// ----------------------------------------------------------------------- inspector

function inspector(scene: SceneDoc, activeFamily: OutputFamily): HTMLElement {
  const bundle = state.bundle!;
  const panel = bundle.panels.find((entry) => entry.position === state.panelPosition) ?? {
    position: state.panelPosition,
    screen: '',
    asset_key: null,
    theme: 'light',
    template_id: null,
    edits: {},
    background: null,
  };
  const selected = currentEditor()?.selection ?? null;
  const layer = scene.layers.find((entry) => entry.id === selected) ?? null;
  const groups: HTMLElement[] = [];

  if (!layer) {
    groups.push(
      h(
        'div',
        { class: 'group' },
        h('span', { class: 'label' }, 'Nothing selected'),
        h('p', { class: 'status' }, 'Click the artwork to pick a layer, or choose one from the list.'),
      ),
    );
  } else {
    const fields: HTMLElement[] = [];
    const numberField = (name: string, value: number, apply: (next: number) => void, step = 0.01) =>
      h(
        'div',
        { class: 'field' },
        h('span', { class: 'name' }, name),
        h('input', {
          type: 'number',
          step: String(step),
          value: String(value),
          'data-prop': name.toLowerCase(),
          onchange: (event: Event) => {
            apply(Number((event.target as HTMLInputElement).value));
            renderScreen();
          },
        }),
      );

    if (layer.type === 'text') {
      const resolved = currentEditor()?.getDrawnScene()?.layers.find((entry) => entry.id === layer.id);
      const shown = resolved?.type === 'text' ? resolved.content : layer.content;
      fields.push(
        h(
          'div',
          { class: 'field' },
          h('span', { class: 'name' }, 'Caption'),
          h('textarea', {
            rows: '3',
            'data-prop': 'content',
            value: shown,
            // On every keystroke, not on blur. A caption is the thing people rewrite most, and making
            // them click away to see it is the difference between typing and guessing. `redrawSoon`
            // coalesces the redraws, so the artwork keeps up without rebuilding the workbench.
            oninput: (event: Event) => {
              const content = (event.target as HTMLTextAreaElement).value;
              currentEditor()?.updateLayer(layer.id, (current) => (current.type === 'text' ? { ...current, content } : current));
              redrawSoon();
            },
          }),
        ),
        h(
          'div',
          { class: 'pair' },
          numberField('X', layer.box.x, (x) => editBox(layer.id, { x })),
          numberField('Y', layer.box.y, (y) => editBox(layer.id, { y })),
        ),
        h(
          'div',
          { class: 'pair' },
          numberField('Width', layer.box.width, (width) => editBox(layer.id, { width })),
          numberField('Height', layer.box.height, (height) => editBox(layer.id, { height })),
        ),
        numberField('Size', layer.style.size, (size) =>
          currentEditor()?.updateLayer(layer.id, (current) =>
            current.type === 'text' ? { ...current, style: { ...current.style, size } } : current,
          ), 0.001),
        h(
          'div',
          { class: 'field' },
          h('span', { class: 'name' }, 'Colour'),
          h('input', {
            type: 'color',
            'data-prop': 'colour',
            value: layer.style.color,
            oninput: (event: Event) => {
              const color = (event.target as HTMLInputElement).value;
              currentEditor()?.updateLayer(layer.id, (current) =>
                current.type === 'text' ? { ...current, style: { ...current.style, color } } : current,
              );
            },
          }),
        ),
        numberField('Rotation', layer.rotation, (rotation) =>
          currentEditor()?.updateLayer(layer.id, (current) => ({ ...current, rotation })), 1),
      );
    }

    /**
     * An element is a layer like any other, so it is edited like one: colour, size, position, rotation,
     * and the words for the ones that carry them. Without this the layer *supported* being recoloured
     * and resized and there was no way to do either.
     */
    if (layer.type === 'element') {
      const definition = elementById(layer.element);
      fields.push(
        h(
          'div',
          { class: 'field' },
          h('span', { class: 'name' }, 'Element'),
          h(
            'select',
            {
              'data-prop': 'element',
              onchange: (event: Event) => {
                const id = (event.target as HTMLSelectElement).value;
                const next = elementById(id);
                // The new element's own proportions, around the same centre: a laurel's box is not a
                // star's, and keeping the old one would stretch whichever arrived.
                const centre = { x: layer.box.x + layer.box.width / 2, y: layer.box.y + layer.box.height / 2 };
                currentEditor()?.updateLayer(layer.id, (current) =>
                  current.type === 'element'
                    ? {
                        ...current,
                        element: id,
                        box: next
                          ? {
                              x: centre.x - next.defaultBox.width / 2,
                              y: centre.y - next.defaultBox.height / 2,
                              width: next.defaultBox.width,
                              height: next.defaultBox.height,
                            }
                          : current.box,
                      }
                    : current,
                );
                renderScreen();
              },
            },
            ...ELEMENTS.map((entry) =>
              h('option', { value: entry.id, selected: entry.id === layer.element ? 'selected' : undefined }, entry.label),
            ),
          ),
        ),
        h(
          'div',
          { class: 'field' },
          h('span', { class: 'name' }, 'Colour'),
          h('input', {
            type: 'color',
            'data-prop': 'element-fill',
            value: layer.fill,
            oninput: (event: Event) => {
              const fill = (event.target as HTMLInputElement).value;
              currentEditor()?.updateLayer(layer.id, (current) =>
                current.type === 'element' ? { ...current, fill } : current,
              );
              redrawSoon();
            },
          }),
        ),
        // Only the elements that carry words get a field for them.
        ...(definition?.labelBox
          ? [
              h(
                'div',
                { class: 'field' },
                h('span', { class: 'name' }, 'Words'),
                h('input', {
                  type: 'text',
                  'data-prop': 'element-label',
                  value: layer.label ?? '',
                  oninput: (event: Event) => {
                    const label = (event.target as HTMLInputElement).value;
                    currentEditor()?.updateLayer(layer.id, (current) =>
                      current.type === 'element' ? { ...current, label } : current,
                    );
                    redrawSoon();
                  },
                }),
              ),
              h(
                'div',
                { class: 'field' },
                h('span', { class: 'name' }, 'Word colour'),
                h('input', {
                  type: 'color',
                  'data-prop': 'element-label-colour',
                  value: layer.labelColor ?? layer.fill,
                  oninput: (event: Event) => {
                    const labelColor = (event.target as HTMLInputElement).value;
                    currentEditor()?.updateLayer(layer.id, (current) =>
                      current.type === 'element' ? { ...current, labelColor } : current,
                    );
                    redrawSoon();
                  },
                }),
              ),
            ]
          : []),
        h(
          'div',
          { class: 'pair' },
          numberField('X', layer.box.x, (x) => editBox(layer.id, { x })),
          numberField('Y', layer.box.y, (y) => editBox(layer.id, { y })),
        ),
        h(
          'div',
          { class: 'pair' },
          numberField('Width', layer.box.width, (width) => editBox(layer.id, { width })),
          numberField('Height', layer.box.height, (height) => editBox(layer.id, { height })),
        ),
        numberField('Rotation', layer.rotation, (rotation) =>
          currentEditor()?.updateLayer(layer.id, (current) =>
            current.type === 'element' ? { ...current, rotation } : current,
          ), 1),
      );
    }

    if (layer.type === 'device') {
      const frame = state.frames.find((entry) => entry.id === layer.frame);
      const view = layer.perspective ?? { tiltX: 0, tiltY: 0, perspective: 1200 };
      const body = (layer.body ?? {}) as NonNullable<typeof layer.body>;
      const is3d = layer.kind === '3d';
      const straight = view.tiltX === 0 && view.tiltY === 0 && layer.transform.rotation === 0;

      fields.push(
        h(
          'div',
          { class: 'field' },
          h('span', { class: 'name' }, 'Model'),
          h(
            'select',
            {
              'data-prop': 'device',
              onchange: (event: Event) => {
                patchDevice(layer.id, { frame: (event.target as HTMLSelectElement).value });
                renderScreen();
              },
            },
            ...state.frames.map((entry) =>
              h('option', { value: entry.id, selected: entry.id === layer.frame ? 'selected' : undefined }, entry.label),
            ),
          ),
        ),
        h(
          'div',
          { class: 'field' },
          h('span', { class: 'name' }, 'Body'),
          h(
            'div',
            { class: 'segmented', role: 'group', 'data-prop': 'kind' },
            tierButton('Flat frame', !is3d, () => patchDevice(layer.id, { kind: 'frame' })),
            tierButton('3D', is3d, () => patchDevice(layer.id, { kind: '3d' })),
          ),
        ),
        // The tier decides what everything below it means, so it says which one is in force instead of
        // leaving the user to discover that their case colour quietly stopped doing anything.
        h(
          'p',
          { class: 'status' },
          is3d
            ? "Built and lit in this tab. Depth is real, and it costs roughly three times a flat frame."
            : "Apple's official bezel, composited flat. Fast, and what the templates are drawn around.",
        ),
      );

      if (is3d) {
        fields.push(
          h(
            'div',
            { class: 'pair' },
            h(
              'div',
              { class: 'field' },
              h('span', { class: 'name' }, 'Case finish'),
              h(
                'select',
                {
                  'data-prop': 'case-finish',
                  onchange: (event: Event) => {
                    patchBody(layer.id, { caseFinish: (event.target as HTMLSelectElement).value });
                    renderScreen();
                  },
                },
                ...['silicone', 'clear', 'leather'].map((finish) =>
                  h('option', { value: finish, selected: body.caseFinish === finish ? 'selected' : undefined }, finish),
                ),
              ),
            ),
            h(
              'div',
              { class: 'field' },
              h('span', { class: 'name' }, 'Case colour'),
              h('input', {
                type: 'color',
                'data-prop': 'case-color',
                value: body.caseColor ?? '#1F2430',
                oninput: (event: Event) => {
                  patchBody(layer.id, { caseColor: (event.target as HTMLInputElement).value });
                  redrawSoon();
                },
              }),
            ),
          ),
          // A percentage of the device's width, which is what the schema stores as a fraction. The
          // first version showed the raw fraction times a thousand — "20" — which means nothing to
          // anyone reading it.
          sliderControl(
            'Case thickness',
            (body.caseThickness ?? 0.02) * 100,
            (value) => patchBody(layer.id, { caseThickness: value / 100 }),
            { min: 0, max: 8, step: 1, unit: '%' },
          ),
        );
      } else {
        fields.push(
          h(
            'div',
            { class: 'field' },
            h('span', { class: 'name' }, 'Case colour'),
            h(
              'select',
              {
                'data-prop': 'variant',
                onchange: (event: Event) => {
                  patchDevice(layer.id, { variant: (event.target as HTMLSelectElement).value });
                },
              },
              ...(frame?.variants ?? []).map((variant) =>
                h('option', { value: variant.key, selected: variant.key === layer.variant ? 'selected' : undefined }, variant.label),
              ),
            ),
          ),
        );
      }

      // Rotation is offered on both tiers because both honour it — the flat one warps, the 3D one
      // turns — and the label says which rather than hiding the control on the tier that fakes it.
      fields.push(
        h(
          'div',
          { class: 'field' },
          h('span', { class: 'name' }, is3d ? 'Rotation' : 'Lean'),
          sliderControl('Turn', view.tiltY, (tiltY) => patchPerspective(layer.id, { tiltY }), DEGREES),
          sliderControl('Tilt', view.tiltX, (tiltX) => patchPerspective(layer.id, { tiltX }), DEGREES),
          sliderControl(
            'Spin',
            layer.transform.rotation,
            (rotation) => patchDevice(layer.id, { transform: { ...layer.transform, rotation } }),
            DEGREES,
          ),
          h(
            'div',
            { class: 'button-row', style: 'margin-top:7px' },
            h(
              'button',
              {
                'data-action': 'straighten',
                disabled: straight ? 'disabled' : undefined,
                onclick: () => {
                  patchPerspective(layer.id, { tiltX: 0, tiltY: 0 });
                  patchDevice(layer.id, { transform: { ...layer.transform, rotation: 0 } });
                  renderScreen();
                },
              },
              'Straighten',
            ),
            !is3d && !straight
              ? h(
                  'p',
                  { class: 'status', style: 'flex:1' },
                  'This lean warps the bezel photograph. The device has no sides in this tier, so its edges cannot appear.',
                )
              : null,
            !is3d && !straight
              ? h(
                  'button',
                  {
                    'data-action': 'promote-3d',
                    title: 'Rebuild this device as a body with real depth',
                    onclick: () => {
                      patchDevice(layer.id, { kind: '3d' });
                      renderScreen();
                    },
                  },
                  'Use the 3D body',
                )
              : null,
          ),
        ),
      );

      if (!is3d) {
        // Focal length belongs to the flat warp only; a 3D body has a camera instead of one.
        fields.push(
          sliderControl(
            'Focal length',
            view.perspective,
            (perspective) => patchPerspective(layer.id, { perspective }),
            { min: 400, max: 3000, step: 50 },
          ),
        );
      }

      fields.push(
        numberField('Scale', layer.transform.scale, (scale) =>
          patchDevice(layer.id, { fitRegion: undefined, transform: { ...layer.transform, scale } }),
        ),
        h(
          'div',
          { class: 'pair' },
          numberField('X', layer.transform.x, (x) => patchDevice(layer.id, { transform: { ...layer.transform, x } })),
          numberField('Y', layer.transform.y, (y) => patchDevice(layer.id, { transform: { ...layer.transform, y } })),
        ),
      );
    }

    groups.push(
      h('div', { class: 'group' }, h('span', { class: 'label' }, layer.name ?? layer.id), ...fields),
    );
  }

  groups.push(
    h(
      'div',
      { class: 'group' },
      h('span', { class: 'label' }, `Panel ${state.panelPosition}`),
      // The template belongs to the panel, not to the set: panel 1 can be a headline over a phone and
      // panel 4 a centred quote with no device at all.
      h(
        'div',
        { class: 'field' },
        h('span', { class: 'name' }, 'Template'),
        h(
          'select',
          {
            'data-action': 'panel-template',
            onchange: (event: Event) => {
              const value = (event.target as HTMLSelectElement).value;
              void setPanelTemplate(state.panelPosition, value === '' ? null : value);
            },
          },
          h(
            'option',
            { value: '', selected: panel.template_id === null ? 'selected' : undefined },
            'Follow the project',
          ),
          ...state.templates.map((entry) =>
            h(
              'option',
              { value: entry.id, selected: panel.template_id === entry.id ? 'selected' : undefined },
              entry.name,
            ),
          ),
        ),
      ),
      h(
        'div',
        { class: 'field' },
        h('span', { class: 'name' }, 'Background'),
        backgroundEditor({
          kind: panel.background ? backgroundKindOf(panel.background) : 'inherit',
          // Seeded with whatever the panel actually shows, so un-linking it changes nothing on screen:
          // the panel simply stops following from that moment, which is what un-linking should mean.
          value: panel.background ?? bundle.background ?? DEFAULT_BACKGROUND,
          inheritLabel: bundle.background ? 'Follows the project' : "Follows the template",
          apply: (next) => void setPanelBackground(state.panelPosition, next),
        }),
      ),
      h(
        'div',
        { class: 'field' },
        h('span', { class: 'name' }, 'Add a layer'),
        h(
          'div',
          { class: 'button-row' },
          h(
            'button',
            {
              style: 'flex:1',
              'data-action': 'add-text-layer',
              onclick: () => void addLayer(state.panelPosition, 'text'),
            },
            'Text',
          ),
          h(
            'button',
            {
              style: 'flex:1',
              'data-action': 'add-device-layer',
              onclick: () => void addLayer(state.panelPosition, 'device'),
            },
            'Device',
          ),
          h(
            'button',
            {
              style: 'flex:1',
              'data-action': 'open-library',
              'aria-pressed': libraryOpen ? 'true' : 'false',
              onclick: () => {
                libraryOpen = !libraryOpen;
                renderScreen();
              },
            },
            'Element',
          ),
        ),
      ),
      libraryOpen ? elementLibrary() : null,
      h(
        'label',
        { class: 'button-row' },
        h('input', {
          type: 'file',
          accept: 'image/png,image/jpeg',
          'data-action': 'replace-capture',
          hidden: 'hidden',
          onchange: (event: Event) => {
            const input = event.target as HTMLInputElement;
            const file = input.files?.[0];
            input.value = '';
            if (file) void replacePanelCapture(state.panelPosition, file);
          },
        }),
        h(
          'button',
          {
            type: 'button',
            style: 'width:100%',
            'data-action': 'pick-capture',
            title: panel.asset_key ? 'Show a different screenshot on this panel' : 'Give this panel a screenshot',
            onclick: (event: Event) =>
              ((event.currentTarget as HTMLElement).parentElement?.querySelector('input') as HTMLInputElement)?.click(),
          },
          icon('swap', 14),
          panel.asset_key ? ' Replace screenshot' : ' Add screenshot',
        ),
      ),
      h(
        'div',
        { class: 'field', style: 'margin-top:11px' },
        h('span', { class: 'name' }, 'Device size'),
        h(
          'select',
          {
            'data-action': 'family',
            onchange: (event: Event) => {
              state.family = (event.target as HTMLSelectElement).value;
              // The whole set is re-drawn: a family changes every panel's canvas size.
              renderScreen();
            },
          },
          ...DEFAULT_FAMILIES.map((entry) =>
            h('option', { value: entry.key, selected: entry.key === state.family ? 'selected' : undefined }, entry.label),
          ),
        ),
      ),
      h(
        'div',
        { class: 'readout', style: 'width:100%', 'data-role': 'canvas-size' },
        h('span', { class: 'size' }, `${activeFamily.canvas.width} × ${activeFamily.canvas.height}`),
      ),
      h('p', { class: 'status', style: 'margin-top:7px' }, activeFamily.appleLabel),
    ),
    h(
      'div',
      { class: 'group' },
      h(
        'div',
        { class: 'button-row' },
        h('button', {
          class: 'primary',
          style: 'flex:1',
          'data-action': 'export',
          disabled: state.busy ? 'disabled' : undefined,
          onclick: () => void exportSet(),
        }, icon('download'), ` ${state.busy ?? 'Export ZIP'}`),
      ),
      h(
        'p',
        { class: 'status', style: 'margin-top:9px' },
        'Rendered here, in this tab, with the renderer the preview uses — so the file is what you see.',
      ),
    ),
  );

  return h('aside', { class: 'inspector', 'data-role': 'inspector' }, ...groups);
}

/** Template layer ids are lowercase slugs; a list of things in a panel reads better capitalised. */
function humanise(value: string): string {
  return value.replace(/[._-]+/g, ' ').replace(/^./, (first) => first.toUpperCase());
}

function editBox(id: string, patch: Partial<{ x: number; y: number; width: number; height: number }>): void {
  currentEditor()?.updateLayer(id, (current) => ('box' in current ? { ...current, box: { ...current.box, ...patch } } : current));
}

/** The template scene one panel draws from, before its own edits and the copy are applied. */
function baseForPosition(position: number): SceneDoc {
  const bundle = state.bundle!;
  return resolveForFamily(templateIdForPanel(bundle, position), state.family);
}

/**
 * A panel, ready to send.
 *
 * No translation: the API takes the shape it returns, so an edit is a change to the object that came
 * back from a read. A camel-to-snake mapping here is what silently dropped the screenshot binding
 * once already.
 */
function wirePanel(panel: PanelSummary): PanelSummary {
  return { ...panel, theme: panel.theme ?? 'light' };
}

/** Persist every panel, which is also how its edits are saved. */
async function savePanels(): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  await api.setPanels(bundle.id, bundle.panels.map(wirePanel));
}

/**
 * Build every Tier 3 device body in a scene, or nothing when the scene has none.
 *
 * Tier 3 is a pre-pass because building a lit 3D body is asynchronous and `renderScene` is not. That
 * separation is what keeps one renderer serving all three environments; it is also why a `3d` layer
 * throws until something calls this, which nothing in the app did.
 */
async function tier3(scene: SceneDoc, draft = false): Promise<Map<string, RenderImage> | undefined> {
  const devices = await prepare3dDevices(scene, editorEnv(), {
    frames: state.frames,
    assets: state.assets,
    ...(draft ? { draft: true } : {}),
  });
  return devices.size > 0 ? devices : undefined;
}

// ------------------------------------------------------------------------ elements

/** Whether the element library is open. It survives a re-render, so adding several is not a chore. */
let libraryOpen = false;

/**
 * One element, drawn small.
 *
 * The preview uses the element's **own box proportions**, because the shapes are drawn in a unit box
 * that is stretched to whatever box the layer has — a laurel authored for a wide box looks like a
 * squashed circle in a square one. Showing it any other way would be showing a different drawing than
 * the one that gets added.
 */
function elementPreview(definition: ElementDefinition, height = 30): HTMLElement {
  const canvas = family().canvas;
  const boxWidth = definition.defaultBox.width * canvas.width;
  const boxHeight = definition.defaultBox.height * canvas.height;
  const aspect = boxWidth / boxHeight;
  const width = Math.max(18, Math.min(96, Math.round(height * aspect)));

  const surface = document.createElement('canvas');
  const scale = 2;
  surface.width = width * scale;
  surface.height = height * scale;
  surface.style.width = `${width}px`;
  surface.style.height = `${height}px`;

  const ctx = surface.getContext('2d')!;
  ctx.scale(surface.width, surface.height);
  // The element's own colour, so the tile is what gets added rather than a black silhouette of it.
  ctx.fillStyle = definition.defaultFill ?? '#191817';

  // A valid layer for the two definitions that read their label colour; the rest ignore it.
  const stub = Layer.parse({
    type: 'element',
    id: 'preview',
    element: definition.id,
    box: { x: 0, y: 0, width: 1, height: 1 },
    fill: definition.defaultFill ?? '#191817',
    ...(definition.defaultLabelColor ? { labelColor: definition.defaultLabelColor } : {}),
    ...(definition.defaultLabelSize ? { labelSize: definition.defaultLabelSize } : {}),
  }) as Parameters<ElementDefinition['draw']>[1];
  definition.draw(ctx, stub, aspect);

  return h(
    'button',
    {
      class: 'element-tile',
      type: 'button',
      title: definition.label,
      'data-element': definition.id,
      onclick: () => void addElement(state.panelPosition, definition),
    },
    surface,
    h('span', { class: 'name' }, definition.label),
  );
}

/** The library: our own elements, grouped, ready to drop onto the selected panel. */
function elementLibrary(): HTMLElement {
  return h(
    'div',
    { class: 'element-library', 'data-role': 'element-library' },
    ...elementsByCategory().map((group) =>
      h(
        'div',
        { class: 'element-group' },
        h('span', { class: 'name' }, group.label),
        h('div', { class: 'element-grid' }, ...group.items.map((item) => elementPreview(item))),
      ),
    ),
  );
}

/** Add an element to one panel. It is a layer like any other: it moves, scales and takes a colour. */
async function addElement(position: number, definition: ElementDefinition): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  const panel = bundle.panels.find((entry) => entry.position === position);
  if (!panel) return;

  const layer = Layer.parse({
    type: 'element',
    id: `${definition.id}-${Date.now().toString(36)}`,
    name: definition.label,
    element: definition.id,
    box: {
      x: 0.5 - definition.defaultBox.width / 2,
      // Sat a little above centre, where a badge usually wants to be.
      y: 0.36,
      width: definition.defaultBox.width,
      height: definition.defaultBox.height,
    },
    ...(definition.defaultFill ? { fill: definition.defaultFill } : {}),
    ...(definition.defaultLabel ? { label: definition.defaultLabel } : {}),
    ...(definition.defaultLabelColor ? { labelColor: definition.defaultLabelColor } : {}),
    ...(definition.defaultLabelSize ? { labelSize: definition.defaultLabelSize } : {}),
  });

  await applyPanels(
    bundle.panels.map((entry) =>
      entry.position === position
        ? {
            ...entry,
            edits: {
              ...entry.edits,
              addLayers: [...(entry.edits.addLayers ?? []), layer],
              removeLayers: (entry.edits.removeLayers ?? []).filter((id) => id !== layer.id),
            },
          }
        : entry,
    ),
    position,
  );
}

// --------------------------------------------------------------------- backgrounds

type BackgroundKind = 'inherit' | 'solid' | 'gradient' | 'radial';

/** The kind a stored background is, for the select. An image background has no controls here yet. */
function backgroundKindOf(background: Background | null | undefined): BackgroundKind {
  if (!background) return 'inherit';
  return background.type === 'solid' || background.type === 'gradient' || background.type === 'radial'
    ? background.type
    : 'inherit';
}

const DEFAULT_BACKGROUND: Background = { type: 'solid', color: '#101828' };

/**
 * The background of a project or of one panel.
 *
 * The two states are expressed by the control rather than by a separate switch: choosing anything other
 * than "follows" writes a background onto the panel, which *is* unlinking it, and choosing "follows"
 * deletes it, which restores the project's. One field, one meaning, and no boolean that can disagree
 * with the value beside it.
 */
function backgroundEditor(options: {
  kind: BackgroundKind;
  /** The colours and geometry the fields show. */
  value: Background;
  inheritLabel: string;
  apply: (next: Background | undefined) => void;
}): HTMLElement {
  const { kind, value, inheritLabel, apply } = options;
  const stops = value.type === 'gradient' || value.type === 'radial' ? value.stops : [];
  const solid = value.type === 'solid' ? value.color : '#101828';
  let first = stops[0]?.color ?? solid;
  let last = stops[stops.length - 1]?.color ?? solid;
  let angle = value.type === 'gradient' ? value.angle : 180;
  let radius = value.type === 'radial' ? value.radius : 0.7;

  const rebuild = (next: BackgroundKind): Background | undefined => {
    if (next === 'inherit') return undefined;
    if (next === 'solid') return { type: 'solid', color: first };
    if (next === 'gradient') {
      return {
        type: 'gradient',
        angle,
        stops: [
          { offset: 0, color: first },
          { offset: 1, color: last },
        ],
      };
    }
    return {
      type: 'radial',
      center: { x: 0.5, y: 0.5 },
      radius,
      stops: [
        { offset: 0, color: first },
        { offset: 1, color: last },
      ],
    };
  };

  const colour = (name: string, current: string, set: (next: string) => void): HTMLElement =>
    h(
      'div',
      { class: 'field' },
      h('span', { class: 'name' }, name),
      h('input', {
        type: 'color',
        value: current,
        'data-prop': `background-${name.toLowerCase().replace(/[^a-z]+/g, '-')}`,
        oninput: (event: Event) => {
          set((event.target as HTMLInputElement).value);
          apply(rebuild(kind === 'inherit' ? 'solid' : kind));
        },
      }),
    );

  const fields: HTMLElement[] =
    kind === 'solid'
      ? [colour('Colour', first, (next) => { first = next; })]
      : kind === 'inherit'
        ? []
        : [
            h(
              'div',
              { class: 'pair' },
              colour(kind === 'radial' ? 'Centre' : 'From', first, (next) => { first = next; }),
              colour(kind === 'radial' ? 'Edge' : 'To', last, (next) => { last = next; }),
            ),
            kind === 'gradient'
              ? sliderControl('Angle', angle, (next) => { angle = next; apply(rebuild('gradient')); }, { min: 0, max: 360, step: 5, unit: '\u00b0' })
              : sliderControl('Size', radius, (next) => { radius = next; apply(rebuild('radial')); }, { min: 0.2, max: 2, step: 0.05 }),
          ];

  return h(
    'div',
    {},
    h(
      'select',
      {
        'data-action': 'background-kind',
        onchange: (event: Event) => apply(rebuild((event.target as HTMLSelectElement).value as BackgroundKind)),
      },
      h('option', { value: 'inherit', selected: kind === 'inherit' ? 'selected' : undefined }, inheritLabel),
      h('option', { value: 'solid', selected: kind === 'solid' ? 'selected' : undefined }, 'Solid colour'),
      h('option', { value: 'gradient', selected: kind === 'gradient' ? 'selected' : undefined }, 'Gradient'),
      h('option', { value: 'radial', selected: kind === 'radial' ? 'selected' : undefined }, 'Glow'),
    ),
    ...fields,
  );
}

/** Set the project background, which every panel that has not unlinked from it follows. */
async function saveProjectBackground(next: Background | undefined): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  try {
    await api.patchProject(bundle.id, { background: next ?? null });
    state.bundle = { ...bundle, background: next ?? null };
    await openProject(bundle.id, state.panelPosition);
  } catch (error) {
    toast((error as Error).message, true);
  }
}

/** Give one panel a background of its own, or put it back on the project's. */
async function setPanelBackground(position: number, next: Background | undefined): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  await applyPanels(
    bundle.panels.map((entry) => (entry.position === position ? { ...entry, background: next ?? null } : entry)),
    position,
  );
}

// ------------------------------------------------------------- panel and layer changes

/**
 * The device layer ids a template provides.
 *
 * Looked up rather than assumed: the empty panel has to remove "the device", and hard-coding the id
 * `device` would remove nothing the day a template names it something else — leaving a device asking
 * for a screenshot nobody uploaded, which is the one thing an empty panel cannot render.
 */
function deviceLayerIds(templateId: string): string[] {
  return resolveForFamily(templateId, state.family)
    .layers.filter((layer) => layer.type === 'device')
    .map((layer) => layer.id);
}

/** An empty panel: a designed slide with no screenshot, and no device left asking for one. */
function emptyPanel(position: number, templateId: string | null = null): PanelSummary {
  const bundle = state.bundle!;
  const template = templateId ?? bundle.template_id;
  return {
    position,
    screen: `panel-${position}`,
    asset_key: null,
    theme: 'light',
    template_id: templateId,
    edits: { patchLayers: {}, addLayers: [], removeLayers: deviceLayerIds(template) },
    background: null,
  };
}

/** Write the panels and come back to a chosen one. Positions are renumbered to 1..n on the way. */
async function applyPanels(panels: PanelSummary[], select: number): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  try {
    await api.setPanels(
      bundle.id,
      [...panels]
        .sort((a, b) => a.position - b.position)
        .map((panel, index) => ({ ...wirePanel(panel), position: index + 1 })),
    );
    await openProject(bundle.id, select);
  } catch (error) {
    toast((error as Error).message, true);
  }
}

async function addPanel(): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  if (bundle.panels.length >= 10) {
    toast('Apple allows at most 10 panels in a set.', true);
    return;
  }
  const position = bundle.panels.length + 1;
  await applyPanels([...bundle.panels, emptyPanel(position)], position);
  toast(`Added an empty panel ${position}.`);
}

async function deletePanel(position: number): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  if (bundle.panels.length <= 1) {
    toast('A set needs at least one panel.', true);
    return;
  }
  const remaining = bundle.panels.filter((panel) => panel.position !== position);
  await applyPanels(remaining, Math.min(position, remaining.length));
}

/** A copy of one panel, inserted straight after it, with its template, edits and background. */
async function duplicatePanel(position: number): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  if (bundle.panels.length >= 10) {
    toast('Apple allows at most 10 panels in a set.', true);
    return;
  }
  const source = bundle.panels.find((panel) => panel.position === position);
  if (!source) return;

  const shifted = bundle.panels.map((panel) => ({
    ...panel,
    position: panel.position > position ? panel.position + 1 : panel.position,
  }));
  const copy: PanelSummary = {
    // The captures are shared, not duplicated: two panels showing the same screen is a layout choice,
    // and re-uploading the same bytes would double the storage for nothing.
    ...source,
    position: position + 1,
    screen: `${source.screen || `panel-${source.position}`}-copy`,
  };
  await applyPanels([...shifted, copy], position + 1);
  toast(`Duplicated panel ${position}.`);
}

/** Add a layer to one panel, which is what makes that panel different from every other panel. */
async function addLayer(position: number, type: 'text' | 'device'): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  const panel = bundle.panels.find((entry) => entry.position === position);
  if (!panel) return;

  const id = `${type}-${Date.now().toString(36)}`;
  const layer =
    type === 'text'
      ? Layer.parse({
          type: 'text',
          id,
          name: 'New caption',
          content: '{{caption.1}}',
          style: { fontFamily: 'Inter', size: 0.045 },
          box: { x: 0.08, y: 0.42, width: 0.84, height: 0.16 },
        })
      : Layer.parse({
          type: 'device',
          id,
          name: 'Device 2',
          // Bound to this panel's own capture when it has one; otherwise the placeholder stays, and the
          // renderer says "no image loaded for capture" — a message about the real problem.
          screen: panel.asset_key ?? 'capture',
          frame: family().defaultFrame,
        });

  const edits = {
    ...panel.edits,
    addLayers: [...(panel.edits.addLayers ?? []), layer],
    // Adding a layer that was previously removed has to un-remove it, or the panel would show nothing.
    removeLayers: (panel.edits.removeLayers ?? []).filter((entry) => entry !== layer.id),
  };
  await applyPanels(
    bundle.panels.map((entry) => (entry.position === position ? { ...entry, edits } : entry)),
    position,
  );
}

/**
 * Take a layer out of one panel.
 *
 * A layer the template provided is removed by naming it; a layer the panel added is simply dropped,
 * because there is nothing underneath it to reveal.
 */
async function removeLayer(position: number, layerId: string): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  const panel = bundle.panels.find((entry) => entry.position === position);
  if (!panel) return;

  const added = panel.edits.addLayers ?? [];
  const edits = {
    ...panel.edits,
    addLayers: added.filter((layer) => layer.id !== layerId),
    removeLayers:
      added.some((layer) => layer.id === layerId)
        ? (panel.edits.removeLayers ?? [])
        : [...new Set([...(panel.edits.removeLayers ?? []), layerId])],
    patchLayers: Object.fromEntries(
      Object.entries(panel.edits.patchLayers ?? {}).filter(([id]) => id !== layerId),
    ),
  };
  await applyPanels(
    bundle.panels.map((entry) => (entry.position === position ? { ...entry, edits } : entry)),
    position,
  );
  currentEditor()?.select(null);
}

/** Give one panel a different template, which is how a set stops looking like one layout ten times. */
async function setPanelTemplate(position: number, templateId: string | null): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  const panel = bundle.panels.find((entry) => entry.position === position);
  if (!panel) return;

  // A different template brings different layer ids, so the edits made against the old one are no
  // longer about anything: keeping them would patch layers that do not exist and remove layers that
  // never did, which reads as "my edits vanished" or worse, as nothing happening at all.
  const reset: PanelSummary = {
    ...panel,
    template_id: templateId,
    edits: { patchLayers: {}, addLayers: [], removeLayers: [] },
  };
  await applyPanels(
    bundle.panels.map((entry) => (entry.position === position ? reset : entry)),
    position,
  );
}

let redrawTimer: number | undefined;
let settleTimer: number | undefined;
let lastDraft = 0;
/**
 * Redraw the selected panel, without rebuilding the board around it.
 *
 * The interval is a frame budget rather than a debounce: at 16 ms a drag can reach sixty frames a
 * second. Each of those redraws is a draft — a Tier 3 body built at half resolution — and the
 * full-quality pass waits for the movement to stop.
 */
function redrawSoon(): void {
  const bundle = state.bundle;
  const screen = activeScreen;
  if (!bundle || !screen) return;
  const draw = (draft: boolean): void => void refreshCard(bundle, screen, state.panelPosition, draft);

  const now = performance.now();
  const due = 16 - (now - lastDraft);
  if (due <= 0) {
    lastDraft = now;
    draw(true);
  } else if (redrawTimer === undefined) {
    redrawTimer = window.setTimeout(() => {
      redrawTimer = undefined;
      lastDraft = performance.now();
      draw(true);
    }, due);
  }

  window.clearTimeout(settleTimer);
  settleTimer = window.setTimeout(() => draw(false), 300);
}

const patchDevice = (id: string, patch: Record<string, unknown>): void => {
  currentEditor()?.updateLayer(id, (layer) => (layer.type === 'device' ? ({ ...layer, ...patch } as typeof layer) : layer));
};
/** The perspective triple is all-or-nothing: a partial object loses the schema's defaults. */
const patchPerspective = (id: string, patch: Record<string, number>): void => {
  currentEditor()?.updateLayer(id, (layer) =>
    layer.type === 'device'
      ? { ...layer, perspective: { tiltX: 0, tiltY: 0, perspective: 1200, ...(layer.perspective ?? {}), ...patch } }
      : layer,
  );
};
const patchBody = (id: string, patch: Record<string, unknown>): void => {
  currentEditor()?.updateLayer(id, (layer) =>
    layer.type === 'device'
      ? ({ ...layer, body: { ...(layer.body ?? {}), ...patch } } as typeof layer)
      : layer,
  );
};

/**
 * A slider with its exact value.
 *
 * Both halves are needed for a continuous control: the slider is how an angle is found, the number is
 * how a specific one is set. Neither alone is usable.
 */
function sliderControl(
  name: string,
  current: number,
  apply: (next: number) => void,
  bounds: { min: number; max: number; step: number; unit?: string },
): HTMLElement {
  const unit = bounds.unit ?? '';
  const value = h('span', { class: 'value' }, `${Math.round(current)}${unit}`);
  return h(
    'div',
    { class: 'control' },
    h('div', { class: 'control-head' }, h('span', { class: 'name' }, name), value),
    h('input', {
      type: 'range',
      min: String(bounds.min),
      max: String(bounds.max),
      step: String(bounds.step),
      value: String(Math.round(current)),
      'data-prop': name.toLowerCase().replace(/[^a-z]+/g, '-'),
      oninput: (event: Event) => {
        const next = Number((event.target as HTMLInputElement).value);
        value.textContent = `${next}${unit}`;
        apply(next);
        redrawSoon();
      },
    }),
  );
}

const DEGREES = { min: -45, max: 45, step: 1, unit: '\u00b0' };

/** The two renderers are mutually exclusive, so this is a switch rather than a checkbox. */
function tierButton(label: string, on: boolean, apply: () => void): HTMLElement {
  return h(
    'button',
    {
      type: 'button',
      'aria-pressed': on ? 'true' : 'false',
      onclick: () => {
        apply();
        repaintWorkbench();
      },
    },
    label,
  );
}

function collectEdits(
  scene: SceneDoc,
  familyKey: string,
  position: number,
): { patchLayers: Record<string, Record<string, unknown>>; addLayers: Layer[] } {
  const bundle = state.bundle!;
  const base = resolveForFamily(templateIdForPanel(bundle, position), familyKey);
  const panel = bundle.panels.find((entry) => entry.position === position);
  const added = new Map((panel?.edits.addLayers ?? []).map((layer) => [layer.id, layer]));

  const patchLayers: Record<string, Record<string, unknown>> = {};
  const addLayers: Layer[] = [];

  for (const layer of scene.layers) {
    /**
     * A layer the panel added is written back **whole**, into `addLayers`.
     *
     * It is not in the template, so there is nothing to diff it against and no patch that could mean
     * anything: a patch keyed by a layer id the template does not have is dropped on the floor. That is
     * where an element's colour, size, position and words were going — the change showed for one frame
     * and the next redraw rebuilt the scene from the untouched `addLayers` and reverted it.
     */
    if (added.has(layer.id)) {
      addLayers.push(layer);
      continue;
    }
    const original = base.layers.find((entry) => entry.id === layer.id);
    if (!original) continue;
    const patch = layerPatch(original, layer);
    if (patch) patchLayers[layer.id] = patch;
  }

  return { patchLayers, addLayers };
}

// ------------------------------------------------------------------------- actions

/** The two formats the API stores, recognised from the bytes rather than the file name. */
const isPngBytes = (bytes: Uint8Array): boolean =>
  bytes.length > 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
const isJpegBytes = (bytes: Uint8Array): boolean =>
  bytes.length > 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;

/**
 * Anything the browser can read becomes a PNG.
 *
 * The API stores PNG and JPEG, which is what App Store Connect takes, and that is not a rule the person
 * uploading should have to know. A WebP or an AVIF exported from a design tool is a perfectly good
 * screenshot, and refusing it for being the wrong container was the difference between "upload works"
 * and "upload is broken". Re-encoding is done once, here, rather than teaching the server a format it
 * would then have to store and convert forever.
 */
async function normaliseImage(file: File, bytes: Uint8Array): Promise<Uint8Array> {
  if (isPngBytes(bytes) || isJpegBytes(bytes)) return bytes;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(new Blob([bytes as unknown as BlobPart], { type: file.type }));
  } catch {
    // Chrome cannot decode HEIC, which is what an iPhone photo is. The server cannot either, so this is
    // the one case where the person has to change something — and now it says so.
    throw new Error('415: not a PNG or JPEG');
  }
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('415: not a PNG or JPEG');
  return new Uint8Array(await blob.arrayBuffer());
}

/**
 * Take screenshots into the project.
 *
 * Each file is reported on its own, because the failures are all file-specific — a HEIC photo, a file
 * over the server's limit — and an upload that fails silently is indistinguishable from one that
 * worked. `replace` starts the set again, which is the only way to change a set that is already full.
 */
async function uploadSet(
  files: FileList | File[] | null,
  mode: 'append' | 'replace' = 'append',
): Promise<void> {
  const bundle = state.bundle;
  // Snapshot immediately: a `FileList` from a file input is live, and anything that clears the input
  // empties it out from under this function.
  const chosenFiles = files ? Array.from(files) : [];
  if (chosenFiles.length === 0 || !bundle) return;

  const existing =
    mode === 'replace' ? [] : [...bundle.panels].sort((a, b) => a.position - b.position);
  const room = 10 - existing.length;
  const chosen = chosenFiles.slice(0, Math.max(0, room));
  const skipped = chosenFiles.length - chosen.length;

  if (chosen.length === 0) {
    toast(`Apple allows ten panels, and this set has ${existing.length}. Use "Start the set again".`, true);
    return;
  }

  state.uploads = [];
  const accepted: PanelSummary[] = [];

  for (const [index, file] of chosen.entries()) {
    const position = existing.length + index + 1;
    const key = `capture.${position}`;
    state.busy = `Uploading ${index + 1} of ${chosen.length}`;
    paintUploads();
    try {
      const bytes = await normaliseImage(file, new Uint8Array(await file.arrayBuffer()));
      await api.uploadAsset(bundle.id, key, bytes, screenNameFrom(file.name));
      accepted.push({
        position,
        screen: screenNameFrom(file.name),
        asset_key: key,
        theme: 'light',
        template_id: null,
        edits: { patchLayers: {}, addLayers: [], removeLayers: [] },
        background: null,
      });
      state.uploads.push({ name: file.name, bytes: file.size, status: 'added' });
    } catch (error) {
      state.uploads.push({
        name: file.name,
        bytes: file.size,
        status: 'rejected',
        reason: describeUploadFailure(error),
      });
    }
    paintUploads();
  }

  if (skipped > 0) {
    state.uploads.push({
      name: `${skipped} more file${skipped === 1 ? '' : 's'}`,
      bytes: 0,
      status: 'rejected',
      reason: 'over the ten-panel limit — Apple allows ten',
    });
  }

  if (accepted.length === 0) {
    state.busy = null;
    paintUploads();
    return;
  }

  state.busy = 'Placing them';
  paintUploads();
  await applyPanels([...existing, ...accepted], existing.length + 1);
  state.busy = null;
  paintUploads();
  toast(
    mode === 'replace'
      ? `Started again with ${accepted.length} panel${accepted.length === 1 ? '' : 's'}.`
      : `Added ${accepted.length} panel${accepted.length === 1 ? '' : 's'}.`,
  );
}

/**
 * Point one panel at a different screenshot.
 *
 * The panel keeps its template, its edits and its background: replacing the picture is not the same
 * as starting the panel again, and people re-shoot a screen and drop it in all the time.
 */
async function replacePanelCapture(position: number, file: File): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  const panel = bundle.panels.find((entry) => entry.position === position);
  if (!panel) return;

  // A fresh key per upload, so the old image is not overwritten while another panel may still be
  // showing it — two panels can share a capture, and replacing one must not change the other.
  const key = `capture.${position}.${Date.now().toString(36)}`;
  const screen = screenNameFrom(file.name);
  state.busy = 'Uploading';
  renderScreen();
  try {
    await api.uploadAsset(bundle.id, key, new Uint8Array(await file.arrayBuffer()), screen);
    state.busy = null;
    await applyPanels(
      bundle.panels.map((entry) =>
        entry.position === position ? { ...entry, screen, asset_key: key } : entry,
      ),
      position,
    );
    toast(`Panel ${position} now shows ${screen}.`);
  } catch (error) {
    state.busy = null;
    toast((error as Error).message, true);
    renderScreen();
  }
}

async function exportSet(): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  if (bundle.panels.length === 0) return toast('Add a screenshot first.', true);

  const activeFamily = family();
  state.busy = 'Rendering';
  renderScreen();
  try {
    // The panels carry their own edits now, so saving the panels is saving the work. There is no
    // project-level edits blob to write, which also means it cannot go stale beside the panels.
    await savePanels();

    const env = createBrowserEnvironment();
    const captures = new Map<string, RenderImage>();
    for (const asset of bundle.assets) {
      captures.set(asset.key, await env.loadImage(`/v1/projects/${bundle.id}/assets/${asset.key}`));
    }

    const panels = await renderSet({
      project: bundle,
      family: state.family,
      locale: 'en-US',
      captures,
      env,
      onProgress: (done, total) => {
        state.busy = `${done}/${total}`;
        renderScreen();
      },
    });

    const problems = panels.flatMap((panel) => panel.problems.map((problem) => `Panel ${panel.position}: ${problem}`));
    if (problems.length > 0) {
      state.busy = null;
      renderScreen();
      toast(problems[0]!, true);
      return;
    }

    const { zip } = zipSet(bundle, state.family, 'en-US', panels, activeFamily.canvas);
    download(`${bundle.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${state.family}.zip`, zip);
    state.busy = null;
    renderScreen();
    toast(`Exported ${panels.length} panel${panels.length === 1 ? '' : 's'} at ${activeFamily.canvas.width}×${activeFamily.canvas.height}.`);
  } catch (error) {
    state.busy = null;
    renderScreen();
    toast((error as Error).message, true);
  }
}

/**
 * Open a project.
 *
 * `screen` defaults to the set view: arriving at a project should show what can be done with it, not
 * one panel of it.
 */
async function openProject(id: string, select?: number, screen: State['screen'] = 'gallery'): Promise<void> {
  const { project } = await api.project(id);
  const env = editorEnv();

  /**
   * Load every capture **before** publishing the project.
   *
   * The first version assigned `state.bundle` straight away and then awaited the images. Anything that
   * watched the bundle for readiness — a test, or a person who had simply started clicking — therefore
   * saw a project that was not there yet, and the `editor = null` at the end of this function wiped
   * whatever they had done in the meantime. The screenshots appeared to ignore clicks for as long as
   * the images took to arrive, and then reset themselves. Publishing last makes the whole switch
   * atomic, so the app is interactive exactly when it is ready.
   *
   * The captured screenshots also have to be in the asset map the editor draws with: without them the
   * device renders empty and the editor disagrees with the export while both look plausible.
   */
  const loaded = new Map(state.assets);
  const missing: string[] = [];
  for (const asset of project.assets) {
    if (loaded.has(asset.key)) continue;
    try {
      loaded.set(asset.key, await env.loadImage(`/v1/projects/${project.id}/assets/${asset.key}`));
    } catch (error) {
      missing.push(`${asset.key}: ${(error as Error).message}`);
    }
  }

  state.assets = loaded;
  state.bundle = project;
  state.panelPosition = select ?? project.panels[0]?.position ?? 1;
  state.screen = screen;
  renderScreen();

  if (missing.length > 0) toast(`Could not load ${missing[0]}`, true);
}

// ------------------------------------------------------------------------ set view

/**
 * Upload, then every template drawn with these screenshots.
 *
 * A person arrives with a folder of screenshots and no idea which layout they want. Drawing the same
 * set through every template, one under another, answers that in one screen rather than by applying
 * each one and undoing it.
 *
 * It is also the only screen with the file list on it, which is where an upload that failed can say
 * so. Every failure here is file-specific — a HEIC photo, a file over the server's limit — and silence
 * is the one outcome that helps nobody.
 */
function galleryScreen(bundle: ProjectBundle): HTMLElement {
  const uploads = h('div', { class: 'uploads', 'data-role': 'uploads' });
  const gallery = h('div', { class: 'gallery', 'data-role': 'gallery' });
  const screen = h(
    'div',
    { class: 'app' },
    topbar(bundle, family()),
    h(
      'main',
      { class: 'set-view' },
      uploadPanel(bundle, uploads),
      h(
        'div',
        { class: 'gallery-head' },
        h('h2', {}, panelsLine(bundle)),
        h('span', { class: 'measure' }, `${state.templates.length} templates`),
      ),
      gallery,
    ),
  );
  queueMicrotask(() => {
    paintUploads();
    void fillGallery(gallery, bundle);
  });
  return screen;
}

/** What the gallery is about to draw, said once. */
function panelsLine(bundle: ProjectBundle): string {
  const count = bundle.panels.length;
  if (count === 0) return 'Add your screenshots';
  return `Every template, drawn with your ${count} screenshot${count === 1 ? '' : 's'}`;
}

/**
 * The upload control, and the list of what happened.
 *
 * Two modes, because there are two things a person means by "add screenshots": add more, or start the
 * set again. The first version only appended, so a full set of ten could not be replaced at all — the
 * upload simply refused, which reads as broken rather than as full.
 */
function uploadPanel(bundle: ProjectBundle, reportList: HTMLElement): HTMLElement {
  const full = bundle.panels.length >= 10;
  const pick = (mode: 'append' | 'replace') => {
    const input = h('input', {
      type: 'file',
      accept: 'image/png,image/jpeg,image/webp,image/avif,image/gif,image/bmp',
      multiple: 'multiple',
      'data-action': 'upload',
      hidden: 'hidden',
      onchange: (event: Event) => {
        const input = event.target as HTMLInputElement;
        // Copied out **before** the input is cleared. `FileList` is live: emptying the input empties the
        // list, so reading it afterwards hands `uploadSet` nothing and the upload silently does nothing
        // at all — which is exactly what it did.
        const files = input.files ? Array.from(input.files) : [];
        input.value = '';
        void uploadSet(files, mode);
      },
    });
    return input;
  };

  const drop = h(
    'label',
    {
      class: 'drop',
      'data-action': 'drop',
      // Dropping files is how this is actually done — a folder of ten screenshots is dragged, not
      // picked one at a time.
      ondragover: (event: DragEvent) => {
        event.preventDefault();
        drop.classList.add('over');
      },
      ondragleave: () => drop.classList.remove('over'),
      ondrop: (event: DragEvent) => {
        event.preventDefault();
        drop.classList.remove('over');
        // The same snapshot rule: a dropped `FileList` is empty by the time the drop handler returns.
        void uploadSet(event.dataTransfer ? Array.from(event.dataTransfer.files) : null, full ? 'replace' : 'append');
      },
    },
    pick(full ? 'replace' : 'append'),
    h('span', { class: 'drop-title' }, full ? 'The set is full — drop new screenshots to start it again' : 'Drop screenshots here'),
    h('span', { class: 'drop-hint' }, 'PNG or JPEG, up to 25 MB each, at most 10 panels.'),
  );

  return h(
    'section',
    { class: 'upload-panel' },
    drop,
    h(
      'div',
      { class: 'button-row' },
      h(
        'button',
        {
          type: 'button',
          'data-action': 'pick-append',
          disabled: full ? 'disabled' : undefined,
          onclick: (event: Event) =>
            ((event.currentTarget as HTMLElement).parentElement?.parentElement?.querySelector('input[data-action="upload"]') as HTMLInputElement)?.click(),
        },
        full ? 'Set full' : 'Add screenshots',
      ),
      h(
        'button',
        {
          type: 'button',
          'data-action': 'pick-replace',
          onclick: (event: Event) => {
            const inputs = (event.currentTarget as HTMLElement).closest('.upload-panel')?.querySelectorAll('input[data-action="upload"]');
            (inputs?.[inputs.length - 1] as HTMLInputElement | undefined)?.click();
          },
        },
        'Start the set again',
      ),
    ),
    reportList,
  );
}

/** The list of what happened to each file, straight from the state. */
function paintUploads(): void {
  const container = document.querySelector('[data-role="uploads"]');
  if (!container) return;
  if (state.uploads.length === 0 && !state.busy) {
    container.replaceChildren();
    return;
  }
  const rows: Node[] = state.busy ? [h('p', { class: 'status busy' }, state.busy)] : [];
  if (state.uploads.length > 0) {
    rows.push(
      h(
      'div',
      { class: 'upload-list' },
      ...state.uploads.map((report) =>
        h(
          'div',
          { class: `upload-row ${report.status}`, 'data-upload': report.name },
          h('span', { class: 'mark' }, report.status === 'added' ? '✓' : '!'),
          h('span', { class: 'name' }, report.name),
          h('span', { class: 'facts' }, `${Math.max(1, Math.round(report.bytes / 1024))} KB`),
            h('span', { class: 'why' }, report.status === 'added' ? 'added' : (report.reason ?? 'rejected')),
          ),
        ),
      ),
    );
  }
  container.replaceChildren(...rows);
}

/**
 * Turn a failure into something a person can act on.
 *
 * A body over nginx's own limit is answered with an HTML page, so the client can see only the status
 * line — which is how an upload that was too large came to read, on screen, as nothing but "413".
 */
function describeUploadFailure(error: unknown): string {
  const message = (error as Error).message ?? String(error);
  if (/^413|too large/i.test(message)) {
    return 'too large — the server takes up to 25 MB per file';
  }
  if (/^415|PNG and JPEG/i.test(message)) {
    return 'not a PNG or JPEG — an iPhone photo is HEIC, so export it first';
  }
  if (/^401|^403/.test(message)) return 'signed out — reload the page';
  if (/^400/.test(message)) return 'the server refused it — see the message above the list';
  return message;
}

async function fillGallery(gallery: HTMLElement, bundle: ProjectBundle): Promise<void> {
  const env = editorEnv();
  const activeFamily = family();
  const panels = [...bundle.panels].sort((a, b) => a.position - b.position).slice(0, 3);

  if (panels.length === 0) {
    gallery.replaceChildren(
      h(
        'p',
        { class: 'empty' },
        'Add a screenshot above and every template appears here, drawn with it.',
      ),
    );
    return;
  }

  gallery.replaceChildren(h('p', { class: 'status busy' }, `Drawing ${state.templates.length} templates…`));
  const cards: HTMLElement[] = [];

  for (const template of state.templates) {
    const strip = h('div', { class: 'gallery-strip' });
    const card = h(
      'article',
      { class: 'gallery-card', 'data-template': template.id },
      strip,
      h(
        'div',
        { class: 'gallery-meta' },
        h('h3', {}, template.name),
        h('p', { class: 'why' }, template.description ?? ''),
        h(
          'button',
          {
            class: 'primary',
            'data-action': 'use-template',
            onclick: () => void useTemplate(template.id, template.name),
          },
          'Use this template',
        ),
      ),
    );
    cards.push(card);

    for (const panel of panels) {
      const scene = previewScene(bundle, template.id, panel.position);
      const devices = await tier3(scene);
      const rendered = renderScene(scene, env, {
        frames: state.frames,
        assets: state.assets,
        ...(devices ? { devices } : {}),
      });
      const surface = document.createElement('canvas');
      surface.width = PREVIEW_BACKING;
      surface.height = Math.round((PREVIEW_BACKING * activeFamily.canvas.height) / activeFamily.canvas.width);
      surface
        .getContext('2d')!
        .drawImage(rendered.canvas.handle as CanvasImageSource, 0, 0, surface.width, surface.height);
      surface.dataset.role = 'preview';
      strip.append(surface);
    }
    // Shown as they finish, so the screen fills in rather than going blank for a second.
    gallery.replaceChildren(...cards);
  }

  if (!gallery.isConnected) return;
  gallery.append(
    h(
      'p',
      { class: 'status' },
      'Picking a template applies it to every panel and opens the board. Each panel can then be given its own layout, or none.',
    ),
  );
}

/**
 * How wide a gallery preview is rendered, before the browser scales it into the card.
 *
 * Twice the 320px the stylesheet shows it at, so it stays crisp on a retina display. Rendering at the
 * display size instead would make every preview soft on exactly the screens this is used on.
 */
const PREVIEW_BACKING = 640;

/**
 * The panel as it would be under a given template.
 *
 * The panel's own edits are dropped, because that is what switching a template does: the patches were
 * keyed to layers the other template had, and the preview has to show what the button will produce
 * rather than something better.
 */
function previewScene(bundle: ProjectBundle, templateId: string, position: number): SceneDoc {
  const source = bundle.panels.find((panel) => panel.position === position);
  if (!source) return scaledScene(resolveForFamily(templateId, state.family), PREVIEW_BACKING);
  // An empty panel keeps its device out: it was made that way, and adopting a template must not put a
  // device back that has no screenshot to show.
  const keepsDeviceOut = source.asset_key === null && (source.edits.removeLayers ?? []).length > 0;
  const project: ProjectBundle = {
    ...bundle,
    template_id: templateId,
    panels: bundle.panels.map((panel) =>
      panel.position === position
        ? {
            ...panel,
            template_id: templateId,
            edits: keepsDeviceOut
              ? { patchLayers: {}, addLayers: [], removeLayers: deviceLayerIds(templateId) }
              : { patchLayers: {}, addLayers: [], removeLayers: [] },
            background: panel.background,
          }
        : panel,
    ),
  };
  // Substituted, like the board and the export do. Without it the preview drew the template's own
  // `{{caption.1}}`, so every card in the gallery showed a token where the headline goes — which is the
  // first thing anyone looks at on this screen.
  const bound = bindPanel(resolveForFamily(templateId, state.family), project, position);
  return scaledScene(substituteTokens(bound, tokenValues(project, 'en-US')), PREVIEW_BACKING);
}

/** Apply one template to the whole set and open the board. */
async function useTemplate(templateId: string, name: string): Promise<void> {
  const bundle = state.bundle;
  if (!bundle) return;
  state.busy = 'Setting up';
  renderScreen();
  try {
    await api.patchProject(bundle.id, { templateId });
    await api.setPanels(
      bundle.id,
      bundle.panels.map((panel) => {
        const keepsDeviceOut = panel.asset_key === null && (panel.edits.removeLayers ?? []).length > 0;
        return wirePanel({
          ...panel,
          template_id: null,
          edits: keepsDeviceOut
            ? { patchLayers: {}, addLayers: [], removeLayers: deviceLayerIds(templateId) }
            : { patchLayers: {}, addLayers: [], removeLayers: [] },
        });
      }),
    );
    state.busy = null;
    state.screen = 'board';
    await openProject(bundle.id, 1, 'board');
    toast(`${name} applied to ${bundle.panels.length} panels.`);
  } catch (error) {
    state.busy = null;
    toast((error as Error).message, true);
    renderScreen();
  }
}

// --------------------------------------------------------------------------- board

/**
 * Every panel at once, live, with the lists and the inspector beside them.
 *
 * A set is judged as a sequence — does the second panel make anyone want to see the third — and that
 * cannot be answered one panel at a time. So this is not a contact sheet of pictures: each card holds
 * its own editor, on its own panel's scene, and clicking one claims that panel for the inspector.
 *
 * The cards render at the card's own resolution rather than at export size. Every measurement in a
 * scene is normalised, so a smaller canvas is the same picture smaller — `scaledScene` adjusts the two
 * things that are not, and the single-panel view remains the place to see it full size.
 */
function boardScreen(bundle: ProjectBundle): HTMLElement {
  const strip = h('div', { class: 'board-strip', 'data-role': 'board' });
  const screen = h(
    'div',
    { class: 'app' },
    topbar(bundle, family()),
    h(
      'div',
      { class: 'workbench' },
      h('nav', { class: 'sidebar', 'data-role': 'sidebar' }),
      h('main', { class: 'board', 'data-role': 'board-scroll' }, strip),
      h('div', { 'data-role': 'inspector-slot' }),
    ),
  );
  queueMicrotask(() => void fillBoard(strip, bundle, screen));
  return screen;
}

/**
 * The same scene at a different pixel size.
 *
 * Layer boxes, text sizes and device placement are all fractions of the canvas, so they scale with it.
 * `perspective` is the exception: it is a focal length in canvas pixels, and leaving it alone would
 * make a lean look different at card size than at export size — which is exactly the kind of quiet
 * disagreement between two views that this product cannot have.
 */
function scaledScene(scene: SceneDoc, width: number): SceneDoc {
  const factor = width / scene.canvas.width;
  if (factor === 1) return scene;
  return {
    ...scene,
    canvas: { width, height: Math.round(scene.canvas.height * factor) },
    layers: scene.layers.map((layer) =>
      layer.type === 'device' && layer.perspective
        ? { ...layer, perspective: { ...layer.perspective, perspective: layer.perspective.perspective * factor } }
        : layer,
    ),
  };
}

/**
 * The scene one panel draws on the board — at **export resolution**, which is the point.
 *
 * The cards were rendered at card size to save memory, and the browser scaled them down. That made the
 * board a picture *of* the export rather than the export, and the check that compares the two found it
 * immediately: 592×1286 on screen against 1320×2868 in the file. The whole promise is that what is on
 * screen is what downloads, and a scaled-down render is a different rasterisation of the same scene —
 * good enough to look at, not good enough to promise. The stylesheet scales the canvas; the pixels in it
 * are the pixels in the archive.
 */
function cardScene(bundle: ProjectBundle, position: number): SceneDoc {
  return bindPanel(baseForPosition(position), bundle, position);
}

async function fillBoard(strip: HTMLElement, bundle: ProjectBundle, screen: HTMLElement): Promise<void> {
  const panels = [...bundle.panels].sort((a, b) => a.position - b.position);
  boardEditors.clear();

  if (panels.length === 0) {
    strip.replaceChildren(h('p', { class: 'empty' }, 'No panels yet. Add screenshots in the editor.'));
    return;
  }

  strip.replaceChildren(h('p', { class: 'status' }, `Drawing ${panels.length} panels\u2026`));

  const cards = panels.map((panel) => {
    const surface = h('div', { class: 'board-surface' });
    const card = h(
      'figure',
      {
        class: `board-card${panel.position === state.panelPosition ? ' selected' : ''}`,
        'data-panel-card': String(panel.position),
        // Clicking the card's frame selects the panel too, so the caption and the margin are not a dead
        // zone that ignores the click the user aimed at the panel.
        onclick: () => {
          if (state.panelPosition === panel.position) return;
          state.panelPosition = panel.position;
          markSelectedCard();
          paintWorkbench(screen, bundle, boardEditors.get(panel.position)?.getScene() ?? cardScene(bundle, panel.position));
        },
      },
      surface,
      h(
        'figcaption',
        {},
        h('span', { class: 'ordinal' }, `Panel ${panel.position}`),
        h('span', {}, panel.template_id ?? 'project template'),
      ),
    );

    const editor = new Editor(surface, {
      frames: state.frames,
      assets: state.assets,
      onMissingAsset: (ref) => toast(`No image loaded for "${ref}".`, true),
      onNeedsRebuild: () => void refreshCard(bundle, screen, panel.position),
      // A click on the artwork claims this panel for the inspector, which is what makes "edit any of
      // them" true rather than "look at any of them".
      onSelectionChange: () => {
        state.panelPosition = panel.position;
        markSelectedCard();
        paintWorkbench(screen, bundle, editor.getScene() ?? cardScene(bundle, panel.position));
      },
      onChange: (scene) => {
        if (!state.bundle) return;
        const { patchLayers, addLayers } = collectEdits(scene, state.family, panel.position);
        // This panel's edits, named explicitly: the board has an editor per panel, so "the current
        // panel" is whatever was last clicked and is not a safe thing to infer.
        state.bundle = {
          ...state.bundle,
          panels: state.bundle.panels.map((entry) =>
            entry.position === panel.position
              ? { ...entry, edits: { ...entry.edits, patchLayers, addLayers } }
              : entry,
          ),
        };
      },
    });
    boardEditors.set(panel.position, editor);
    return card;
  });

  strip.replaceChildren(...cards);

  // Scenes after the cards are in the document, so each canvas measures and paints against a real box.
  for (const panel of panels) {
    await refreshCard(bundle, screen, panel.position);
  }
  paintWorkbench(screen, bundle, boardEditors.get(state.panelPosition)?.getScene() ?? cardScene(bundle, state.panelPosition));
}

/** Draw one board card, building its Tier 3 body if it has one. `draft` is the cheap version. */
async function refreshCard(
  bundle: ProjectBundle,
  screen: HTMLElement,
  position: number,
  draft = false,
): Promise<void> {
  const editor = boardEditors.get(position);
  if (!editor) return;
  const started = performance.now();
  const scene = cardScene(bundle, position);
  const body = await tier3(scene, draft);
  const built = performance.now();
  editor.setScene(
    scene,
    family(),
    (document) => substituteTokens(document, tokenValues(bundle, 'en-US')),
    body,
  );
  // Timings rather than a stopwatch on the outside. A measurement that reads the canvas back every frame
  // measures its own readback as much as the render, and the split between building a Tier 3 body and
  // compositing it is what says which half to fix.
  renderTimings.push({
    at: performance.now(),
    body: Math.round(built - started),
    composite: Math.round(performance.now() - built),
  });
  if (renderTimings.length > 240) renderTimings.shift();
  // The layer list belongs to the selected panel, so it follows a panel that just re-drew.
  if (state.panelPosition === position && screen.isConnected) {
    paintWorkbench(screen, bundle, editor.getScene() ?? scene);
  }
}

/** The ring around the selected card. */
function markSelectedCard(): void {
  for (const card of document.querySelectorAll<HTMLElement>('.board-card')) {
    card.classList.toggle('selected', Number(card.dataset.panelCard) === state.panelPosition);
  }
}

/**
 * Repaint the lists and the inspector, leaving the board's editors where they are.
 *
 * `renderScreen` rebuilds the board, which means new editors — and a new editor has no selection, so
 * clicking a layer threw the selection away on the way to showing it. Anything that changes what the
 * lists say rather than what the artwork contains belongs here.
 */
function repaintWorkbench(): void {
  const bundle = state.bundle;
  const screen = activeScreen;
  const scene = currentEditor()?.getScene();
  if (!bundle || !screen || !scene) return;
  paintWorkbench(screen, bundle, scene);
}

/** Fill the workbench's lists and inspector from the scene the selected panel is showing. */
function paintWorkbench(screen: HTMLElement, bundle: ProjectBundle, scene: SceneDoc): void {
  screen
    .querySelector('[data-role="sidebar"]')
    ?.replaceChildren(panelsGroup(bundle), layersGroup(scene), projectGroup(bundle));
  screen.querySelector('[data-role="inspector-slot"]')?.replaceChildren(inspector(scene, family()));
}

// ----------------------------------------------------------------------- rendering

function topbar(bundle: ProjectBundle, activeFamily: OutputFamily): HTMLElement {
  return h(
    'header',
    { class: 'topbar' },
    h('span', { class: 'wordmark' }, 'App Screenshots'),
    h('span', { class: 'divider' }),
    h('span', { class: 'project' }, bundle.name),
    h(
      'div',
      { class: 'segmented', role: 'group' },
      h(
        'div',
        { class: 'segmented', role: 'group' },
        ...(['gallery', 'board'] as const).map((screen) =>
          h(
            'button',
            {
              type: 'button',
              'data-view': screen,
              'aria-pressed': state.screen === screen ? 'true' : 'false',
              onclick: () => {
                state.screen = screen;
                renderScreen();
              },
            },
            screen === 'gallery' ? 'Templates' : 'Board',
          ),
        ),
      ),
    ),
    h('span', { class: 'spacer' }),
    h(
      'span',
      { class: 'readout' },
      h('span', { class: 'size' }, `${activeFamily.canvas.width} × ${activeFamily.canvas.height}`),
      h('span', { class: 'slot' }, activeFamily.label),
    ),
    h('button', { class: 'quiet', 'data-action': 'back', onclick: () => { state.bundle = null; state.uploads = []; renderScreen(); } }, 'All projects'),
    h('span', { class: 'user' }, state.user?.email ?? ''),
    h('button', {
      class: 'quiet',
      'data-action': 'logout',
      onclick: () => void (async () => {
        await api.logout();
        state.user = null;
        state.bundle = null;
        renderScreen();
      })(),
    }, 'Sign out'),
  );
}

function renderScreen(): void {
  if (!state.user) {
    root.replaceChildren(authScreen());
    return;
  }
  if (!state.bundle) {
    root.replaceChildren(h('div', { class: 'app' }, simpleBar(), h('main', {}, projectsScreen())));
    return;
  }
  if (state.screen === 'board') {
    activeScreen = boardScreen(state.bundle);
    root.replaceChildren(activeScreen);
    return;
  }
  // The board's editors go with the board, so a stale card cannot answer for a panel that is no longer
  // on screen — and their canvases are released rather than held for a view nobody is looking at.
  boardEditors.clear();
  activeScreen = galleryScreen(state.bundle);
  root.replaceChildren(activeScreen);
}

function simpleBar(): HTMLElement {
  return h(
    'header',
    { class: 'topbar' },
    h('span', { class: 'wordmark' }, 'App Screenshots'),
    h('span', { class: 'spacer' }),
    h('span', { class: 'user' }, state.user?.email ?? ''),
    h('button', {
      class: 'quiet',
      'data-action': 'logout',
      onclick: () => void (async () => {
        await api.logout();
        state.user = null;
        state.bundle = null;
        renderScreen();
      })(),
    }, 'Sign out'),
  );
}

// ---------------------------------------------------------------------------- boot

async function boot(): Promise<void> {
  try {
    state.user = await api.me();
  } catch {
    state.user = null;
    renderScreen();
    return;
  }

  const [{ templates }, { projects }, frameLibrary] = await Promise.all([api.templates(), api.projects(), frames()]);
  state.templates = templates;
  state.projects = projects;
  state.frames = frameLibrary;
  state.assets = await frameImages(createBrowserEnvironment());
  renderScreen();
}

void boot();

// Exposed so the browser tests drive the same functions a user's clicks do.
declare global {
  interface Window {
    __app: {
      state: State;
      boot: () => Promise<void>;
      openProject: (id: string) => Promise<void>;
      exportSet: () => Promise<void>;
      uploadSet: (files: FileList | File[] | null) => Promise<void>;
      screenNameFrom: typeof screenNameFrom;
      /** The selected panel's editor, so a browser test can read what is selected. */
      editor: () => Editor | null;
      /** Recent render timings, for the performance measurement. */
      renderTimings: () => { at: number; body: number; composite: number }[];
    };
  }
}

window.__app = {
  state,
  boot,
  openProject,
  exportSet,
  uploadSet,
  screenNameFrom,
  editor: () => currentEditor(),
  renderTimings: () => [...renderTimings],
};
